import React from 'react';
import { Link } from 'react-router-dom';
import {
  Layout, BarChart3, DollarSign, Calculator, UserCheck, Smartphone, Palette, Sparkles, Heart, Play, BookOpen,
} from 'lucide-react';

interface IndexItem {
  path: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  section: string;
}

const items: IndexItem[] = [
  {
    path: '/restaurant/mockups',
    title: 'Server Mockups Hub',
    description: 'All server-facing gamification mockups — earnings, leaderboards, rewards, and more',
    icon: Layout,
    section: 'Mockup Collections',
  },
  {
    path: '/restaurant/mockups/gen-bbq-analysis',
    title: 'Gen BBQ Deep Analysis',
    description: 'Comprehensive server performance breakdown for Gen Korean BBQ',
    icon: BarChart3,
    section: 'Reports & Tools',
  },
  {
    path: '/restaurant/mockups/jess-coaching-plan',
    title: 'Project Jess — 90-Day Coaching Plan',
    description: '90-day star server transformation plan with weekly goals and milestones',
    icon: UserCheck,
    section: 'Reports & Tools',
  },
  {
    path: '/restaurant/mockups/roi-calculator',
    title: 'Bespoke ROI Calculator',
    description: 'Interactive ROI model — plug in restaurant numbers and see projected return',
    icon: Calculator,
    section: 'Reports & Tools',
  },
  {
    path: '/restaurant/mockups/server-app-styles',
    title: 'Server App — Style Explorations',
    description: '3×3 grid of Fitbit, Strava, and Snackpass style mockups for The Grove design partner meeting',
    icon: Smartphone,
    section: 'Reports & Tools',
  },
  {
    path: '/restaurant/mockups/server-app-styles-2',
    title: 'Server App — Style Explorations II',
    description: '3×3 grid of Duolingo, Spotify, and Cash App styles — post-shift recap, skills, achievements',
    icon: Palette,
    section: 'Reports & Tools',
  },
  {
    path: '/restaurant/mockups/server-app-styles-3',
    title: 'Server App — Style Explorations III',
    description: '3×3 grid of Fitbit, Strava, and Snackpass styles — pre-shift briefing, live momentum, AI coaching',
    icon: Sparkles,
    section: 'Reports & Tools',
  },
  {
    path: '/restaurant/mockups/server-app-styles-4',
    title: 'Server App — Style Explorations IV',
    description: '3×4 grid — interview-informed redesign: no upsell language, private stats, team goals, menu prep quiz',
    icon: Heart,
    section: 'Reports & Tools',
  },
  {
    path: '/restaurant/mockups/server-app-prototype',
    title: 'Server App — Interactive Prototype',
    description: 'Duolingo-inspired interactive prototype for The Grove SF with XP, shift recording, and menu prep',
    icon: Play,
    section: 'Reports & Tools',
  },
  {
    path: '/restaurant/mockups/duolingo-prototype',
    title: 'Server App — Duolingo Prototype',
    description: 'Duolingo-style interactive prototype with lessons, streaks, and gamified hospitality training',
    icon: BookOpen,
    section: 'Reports & Tools',
  },
];

const sections = ['Mockup Collections', 'Reports & Tools'];

const MockupIndex: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="text-2xl font-bold mb-1">Bespoke Internal Demos</h1>
        <p className="text-sm text-gray-500 mb-8">All mockups, reports, and tools. Login required.</p>

        {sections.map((section) => {
          const sectionItems = items.filter(i => i.section === section);
          if (sectionItems.length === 0) return null;
          return (
            <div key={section} className="mb-8">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{section}</h2>
              <div className="space-y-1">
                {sectionItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-900 transition-colors group no-underline !text-inherit"
                  >
                    <item.icon className="w-4 h-4 text-gray-600 group-hover:text-indigo-400 transition-colors flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-200 group-hover:text-white transition-colors">{item.title}</div>
                      <div className="text-xs text-gray-600 truncate">{item.description}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MockupIndex;
